import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  DOCKED_PREVIEW_GAP_PX,
  useDockedHeight,
} from '../../shared/results_page/docked_preview.js';
import type { MapOverlays } from '../../shared/results_page/map_fit.js';
import type { HotelOfferView } from '../models.js';
import type { HotelMapProps } from './hotel_map.js';
import { HotelOfferCard, type HotelOfferPhotoCopy } from './hotel_offer_card.js';

/**
 * A PRÉVIA do hotel sobre o mapa — o card que o pino abre. Saiu de
 * `hotel_map.tsx` (teto de linhas) com as duas ancoragens: no pino, para o
 * mouse; no pé do mapa, para o toque (#3108).
 */
export const PIN_BODY_CLASS = 'hui-hotel-map-pin-body';

/** O que a prévia sabe de si: o hotel e ONDE nasce — ancorada no pino (o
 *  hover do mouse, em coordenadas de viewport) ou no pé do mapa (o toque do
 *  celular, `HotelMapProps.dockedPreview`). */
export type HotelPreview =
  | {
      hotelId: string;
      placement: 'above' | 'below';
      x: number;
      y: number;
      /** Onde o card monta: o diálogo em volta do mapa (fora dele o Base UI
       *  esconde tudo e o card ficaria atrás da tela), senão o `body`. */
      host: HTMLElement | null;
    }
  | { hotelId: string; placement: 'docked' };

export function dockedPreview(hotelId: string): HotelPreview {
  return { hotelId, placement: 'docked' };
}

/** O que a prévia ancorada no pé precisa do mapa: o rótulo do X, a distância
 *  do pé até o card — a folha (`overlays.sheet`) mais o vão — e a faixa de
 *  fotos (`photoCopy`), que é onde o dedo passa as fotos (#3108). A prévia do
 *  mouse segue com a foto pequena, como sempre esteve. */
export function dockedPreviewProps(
  dockedPreview: HotelMapProps['dockedPreview'],
  overlays: MapOverlays,
  photoCopy: HotelOfferPhotoCopy | undefined,
): { closeLabel: string; dockedBottom: number; photoCopy?: HotelOfferPhotoCopy } | undefined {
  if (!dockedPreview) return undefined;
  return {
    closeLabel: dockedPreview.closeLabel,
    dockedBottom: (overlays.sheet ?? 0) + DOCKED_PREVIEW_GAP_PX,
    ...(photoCopy ? { photoCopy } : {}),
  };
}

export function useHotelPreview() {
  const [preview, setPreview] = useState<HotelPreview>();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== undefined) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  }, []);
  const open = useCallback(
    (nextPreview: HotelPreview) => {
      cancelClose();
      setPreview(nextPreview);
    },
    [cancelClose],
  );
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setPreview(undefined);
    }, 150);
  }, [cancelClose]);
  const close = useCallback(() => {
    setPreview(undefined);
  }, []);
  useEffect(() => cancelClose, [cancelClose]);
  return { preview, open, close, scheduleClose, cancelClose };
}

export function markerPreview(element: HTMLButtonElement): HotelPreview {
  // A caixa é a da PÍLULA, não a do botão: o botão tem tamanho zero e senta na
  // coordenada (é o que ancora o bico no ponto exato), então medi-lo devolveria
  // um retângulo vazio e o card nasceria em cima do pino em vez de acima dele.
  const body = element.querySelector<HTMLElement>(`.${PIN_BODY_CLASS}`) ?? element;
  const markerBounds = body.getBoundingClientRect();
  // Coordenadas de VIEWPORT, não do mapa: o card vive num portal (o retângulo
  // do mapa é `overflow: hidden` e não pode cortá-lo), centralizado no pino, e
  // só sai do centro quando bate na borda da TELA — a validação de design pediu
  // exatamente o sangramento. `clientWidth` e não `innerWidth`: a barra de
  // rolagem não é tela útil.
  const viewportWidth = document.documentElement.clientWidth;
  const previewWidth = Math.min(21 * 16, viewportWidth - 16);
  const desiredLeft = markerBounds.left + markerBounds.width / 2 - previewWidth / 2;
  const maxLeft = Math.max(8, viewportWidth - previewWidth - 8);
  const placement = markerBounds.top >= 9 * 16 ? 'above' : 'below';
  return {
    hotelId: element.dataset.hotelId ?? '',
    x: Math.min(Math.max(8, desiredLeft), maxLeft),
    y: placement === 'above' ? markerBounds.top : markerBounds.bottom,
    placement,
    host: element.closest<HTMLElement>('[role="dialog"]'),
  };
}

export function HotelMapPreview({
  preview,
  offer,
  detailsLabel,
  selectLabel,
  photoCopy,
  selectionLocked,
  onMouseEnter,
  onMouseLeave,
  onHotelSelect,
  onDetailsOpen,
  onClose,
  closeLabel,
  dockedBottom,
  onDockedResize,
}: Pick<
  HotelMapProps,
  | 'detailsLabel'
  | 'selectLabel'
  | 'photoCopy'
  | 'selectionLocked'
  | 'onHotelSelect'
  | 'onDetailsOpen'
> & {
  readonly preview: HotelPreview;
  readonly offer: HotelOfferView;
  readonly onMouseEnter: () => void;
  readonly onMouseLeave: () => void;
  readonly onClose: () => void;
  /** Só na prévia ancorada no pé: o rótulo do X. */
  readonly closeLabel?: string;
  /** Só na prévia ancorada no pé: a distância do pé do mapa até o card. */
  readonly dockedBottom?: number;
  /** Só na prévia ancorada no pé: a altura viva do card (`undefined` quando ele
   *  sai de cena), para o mapa tirar os controles de baixo dele. */
  readonly onDockedResize?: (height: number | undefined) => void;
}): ReactNode {
  const dockedRef = useDockedHeight(onDockedResize);
  const card = (
    <HotelOfferCard
      offer={offer}
      detailsLabel={detailsLabel}
      selectLabel={selectLabel}
      action="details"
      surface="map"
      showDetailsButton
      {...(photoCopy ? { photoCopy } : {})}
      busy={selectionLocked}
      onHotelSelect={onHotelSelect}
      onDetailsOpen={(hotelId) => {
        onClose();
        onDetailsOpen(hotelId);
      }}
    />
  );
  // A prévia ANCORADA NO PÉ (#3108): filha do mapa, não portal — ela vive
  // dentro do retângulo dele, logo acima da folha (`dockedBottom`). O card
  // INTEIRO é o alvo do toque, foto inclusive; os botões dele (setas da faixa,
  // o X, "ver hotel") respondem sozinhos e não passam por aqui. O toque no
  // card é atalho de ponteiro: no teclado o caminho é o "ver hotel" do card,
  // então o contêiner sai da árvore de acessibilidade (`role="presentation"`).
  if (preview.placement === 'docked') {
    const canOpen = !selectionLocked && offer.detailsAvailable;
    return (
      <div
        ref={dockedRef}
        className="hui-hotel-map-preview"
        data-placement="docked"
        data-hotel-id={offer.id}
        role="presentation"
        style={{ bottom: dockedBottom }}
        onClick={(event) => {
          if (event.target instanceof Element && event.target.closest('button')) return;
          if (!canOpen) return;
          onClose();
          onDetailsOpen(offer.id);
        }}
      >
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className="hui-hotel-map-preview-close rounded-full shadow-sm"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <XIcon aria-hidden="true" />
        </Button>
        {card}
      </div>
    );
  }

  // PORTAL, e não filho do mapa: o mapa é `overflow: hidden` com borda
  // arredondada, e o pedido da validação é justamente o contrário — o card
  // sangra para fora do retângulo do mapa e só se reposiciona no limite da
  // tela. O alvo é o diálogo em volta do mapa (a tela em diálogo da lista ou
  // do mapa cheio), senão o `body`. O `genui2` vai junto porque o portal pode
  // sair do escopo do DS: sem a classe, o card perde todos os tokens.
  return createPortal(
    <div
      className="genui2 hui-hotel-map-preview"
      data-placement={preview.placement}
      role="presentation"
      style={{ left: preview.x, top: preview.y }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onMouseEnter}
      onBlur={onMouseLeave}
    >
      {card}
    </div>,
    preview.host ?? document.body,
  );
}
